import { Injectable } from '@angular/core';
import { SyncCryptService } from '../../../core/crypt/sync-crypt.service';
import { LoggerService } from '../../../core/logger.service';
import { ERROR_CODES, ErrCode } from '../../../shared/models';
import { environment } from '../../../../environments/environment';
import { IDownloadMethod, IStoreData, ITransferItemDownload } from 'src/sync';
import { DownloadChunkFetchOptions, DownloadChunkFetchService } from './download-chunk-fetch.service';

@Injectable({
  providedIn: 'root',
})
export class DownloadWorkerService implements IDownloadMethod {
  constructor(
    private crypt: SyncCryptService,
    private downloadChunkFetch: DownloadChunkFetchService,
    private log: LoggerService,
  ) {}

  public abort(): void {
    /* no-op: worker terminates itself */
  }

  public async getChunk(
    tItem: ITransferItemDownload,
    offset: number,
    reqLength: number,
    retry = 0,
    options?: DownloadChunkFetchOptions,
  ): Promise<IStoreData> {
    const response = await this.downloadChunkFetch.fetchChunk(tItem, offset, reqLength, retry, options);

    return new Promise<IStoreData>((resolve, reject) => {
      const workObj = new Worker(environment.jslibpath + 'filedecrypt-worker.js?d=' + Date.now());

      // A worker that dies before posting back (importScripts failing offline) would otherwise
      // leave this promise pending forever and freeze the whole transfer.
      workObj.addEventListener('error', (ev: ErrorEvent) => {
        this.log.error(`filedecrypt-worker failed to run: ${ev.message}`);
        workObj.terminate();
        reject(new ErrCode(ERROR_CODES.DOWNLOAD_CONNECTION_ERROR));
      });

      workObj.addEventListener(
        'message',
        (ev: MessageEvent) => {
          const agentCmd = ev.data.agentcmd,
            cmdData = ev.data.data;
          switch (agentCmd) {
            case 'error':
              this.log.error(`Received error ${agentCmd} with data: ${JSON.stringify(cmdData)}`);
              const errcode = parseInt(ev.data.errcode, 10) || 1000;
              this.log.error(errcode + ' error code download-chunk-worker');
              workObj.terminate();
              reject(new ErrCode(errcode));
              break;
            case 'log':
              this.log.info(cmdData);
              break;
            case 'runFinished':
              workObj.terminate();
              resolve(<IStoreData>cmdData);
              break;
            default:
              this.log.error(`${agentCmd} does not exist`);
              workObj.terminate();
              reject(new ErrCode(ERROR_CODES.ACTION_DOES_NOT_EXIST));
              break;
          }
        },
        false,
      );

      workObj.postMessage({
        workercmd: 'run',
        data: {
          response: response,
          tItem: tItem,
          jsliburl: environment.jslibpath,
          datakey: this.crypt.bytesToB64(tItem.data_key),
          offset: offset,
          reqLength: reqLength,
        },
      });
    });
  }
  public completed(): void {
    /* no-op: required by IDownloadMethod */
  }
}
